<script setup lang="ts">
import { usePensionOptionsStore } from "../stores/pensionOptions";
import { Question, RadioSet, Radio } from "./fields";
import { tooltips } from "../tooltips/pensionOptionsTooltips";
import { pensionOptionsValidator } from "../utils/validators";

const store = usePensionOptionsStore();
const validator = pensionOptionsValidator(store);

</script>

<template>
    <div
        v-if="store.pensionType === false"
        :class="store.pensionType === false ? 'overlay-error' : ''"
    >
        <div class="row">
            <div class="alert alert-danger-2 text-center zInd2">
                <p>
                    If you are not using one of these pension fund types, then we will be unable to help you.
                    If you would like to investigate the possibility of purchasing a lifetime annuity, please visit <a
                        href="https://www.moneyhelper.org.uk/en/pensions-and-retirement"
                        class="link-light"
                        target="_blank"
                        rel="noopener noreferrer"
                    >MoneyHelper</a>.
                </p>
            </div>
        </div>
    </div>
    <Question
        type="group"
        name="pensionType"
        label="Is the type of pension fund(s) you will be using to purchase your annuity listed below?"
        :tooltip="tooltips.pensionType"
        :class="store.pensionType === false ? 'overlay-question' : ''"
    >
        <RadioSet>
            <Radio
                v-model="store.pensionType"
                title="Yes"
                :value="true"
                :validation="validator.pensionType"
            />
            <Radio
                v-model="store.pensionType"
                title="No"
                :value="false"
                :validation="validator.pensionType"
            />
        </RadioSet>
    </Question>
    <div class="container text-left list-text col-11 col-lg-11 col-md-11 p-0">
        <div class="row">
            <ul class="col-md-6">
                <li>Individual Personal Pension</li>
                <li>Individual Stakeholder Personal Pension</li>
                <li>Defined Contribution Pension/Scheme</li>
                <li> Self Invested Personal Pension</li>
                <li> Section 226 Retirement Annuity</li>
                <li> Free Standing Additional Voluntary Contribution (FSAVC)</li>
            </ul>
            <ul class="col-md-6">
                <li>Appropriate Personal Pension Scheme</li>
                <li>Group Personal Pension</li>
                <li>Group Stakeholder Personal Pension</li>
                <li> Personal Pension but not sure which scheme</li>
            </ul>
        </div>
    </div>
    <div
        v-if="store.fundTransferred"
        :class="store.fundTransferred ? 'overlay-error' : ''"
    >
        <div class="row">
            <div class="alert alert-danger-2 text-center zInd2">
                <p>We are unable to provide quotes for funds with guaranteed benefits. Please contact your existing pension fund provider.</p>
            </div>
        </div>
    </div>
    <Question
        type="group"
        name="fundTransferred"
        label="Does your pension fund have any guaranteed benefits such as a guaranteed minimum pension?"
        :tooltip="store.fundTransferred ? tooltips.fundTransferredYes : tooltips.fundTransferredNo"
        :class="store.fundTransferred ? 'overlay-question' : ''"
    >
        <RadioSet>
            <Radio
                id="pension_fund_transferred_0"
                v-model="store.fundTransferred"
                title="Yes"
                :value="true"
                :validation="validator.fundTransferred"
            />
            <Radio
                id="pension_fund_transferred_1"
                v-model="store.fundTransferred"
                title="No"
                :value="false"
                :validation="validator.fundTransferred"
            />
        </RadioSet>
    </Question>
    <div
        v-if="store.guaranteeAnnuityRate"
        :class="store.guaranteeAnnuityRate ? 'overlay-error' : ''"
    >
        <div class="row">
            <div class="alert alert-danger-2 text-center zInd2">
                <p>We are unable to provide quotes for funds with guaranteed benefits. Please contact your existing pension fund provider.</p>
            </div>
        </div>
    </div>
    <Question
        type="group"
        name="guaranteeAnnuityRate"
        label="Does your pension policy provide you with a guaranteed annuity rate?"
        :tooltip="store.guaranteeAnnuityRate ? tooltips.guaranteeAnnuityRateYes : tooltips.guaranteeAnnuityRateNo"
        :class="store.guaranteeAnnuityRate ? 'overlay-question' : ''"
    >
        <RadioSet>
            <Radio
                id="existing_guarantee_annuity_rate_0"
                v-model="store.guaranteeAnnuityRate"
                title="Yes"
                :value="true"
                :validation="validator.guaranteeAnnuityRate"
            />
            <Radio
                id="existing_guarantee_annuity_rate_1"
                v-model="store.guaranteeAnnuityRate"
                title="No"
                :value="false"
                :validation="validator.guaranteeAnnuityRate"
            />
        </RadioSet>
    </Question>
    <div
        v-if="store.moreThanTwentyFive"
        :class="store.moreThanTwentyFive ? 'overlay-error' : ''"
    >
        <div class="row">
            <div class="alert alert-danger-2 text-center zInd2">
                <p>We are unable to provide quotes for pension funds eligible for more than a 25% tax-free lump sum. If you are entitled to more than a 25% tax-free lump sum, you should contact your pension fund provider or speak to a financial adviser. This applies even if you do not want more than the 25%.</p>
            </div>
        </div>
    </div>
    <Question
        type="group"
        name="moreThanTwentyFive"
        label="You can usually take up to 25% tax-free lump sum from your pension fund.
        Are you entitled to more than 25%?"
        :tooltip="tooltips.moreThanTwentyFive"
        :class="store.moreThanTwentyFive ? 'overlay-question' : ''"
    >
        <RadioSet>
            <Radio
                id="more_than_twenty_five_tfc_0"
                v-model="store.moreThanTwentyFive"
                title="Yes"
                :value="true"
                :validation="validator.moreThanTwentyFive"
            />
            <Radio
                id="more_than_twenty_five_tfc_1"
                v-model="store.moreThanTwentyFive"
                title="No"
                :value="false"
                :validation="validator.moreThanTwentyFive"
            />
        </RadioSet>
    </Question>
</template>
